/**
 * =============================================================================
 * 🛡️ ProtectedRoute.tsx — Canonical Role-Based Route Enforcement (CTO Standard)
 * =============================================================================
 *
 * 🔐 Purpose:
 * Guard tenant-scoped frontend routes based on:
 *   (1) session (JWT validity),
 *   (2) user role match,
 *   (3) tenantSlug router param (URL is the ONLY source of truth).
 *
 * 📌 Guarantees / Invariants (LOCKED):
 * - ProtectedRoute is the ONLY auth gate (no other component may redirect to login).
 * - URL param (:tenantSlug) is canonical — storage is NOT consulted here.
 * - Never silently invents or heals tenant context.
 * - On unauthenticated → redirect to "/:tenantSlug/login".
 * - On unauthorized → redirect to "/:tenantSlug/403".
 * - Never renders children during redirect.
 *
 * =============================================================================
 */

import React, { useMemo } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { getUserFromToken } from "@/utils/auth";

interface ProtectedRouteProps {
  allowedRoles: string[];        // e.g. ['ddo', 'finance', 'management']
  children: React.ReactElement;  // element to render on success
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowedRoles,
  children,
}) => {
  const user = getUserFromToken();
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  const location = useLocation();

  /**
   * 🧠 Canonical session truth
   * user = decoded JWT payload { sub, role, exp, tenant_id, ... }
   */
  const isAuthenticated = !!user?.role;
  const isAuthorized = isAuthenticated && allowedRoles.includes(user.role);

  /**
   * 🧭 Canonical tenantSlug resolution (LOCKED)
   * URL param ONLY. Storage is intentionally ignored here.
   */
  const canonicalSlug = useMemo(() => {
    if (tenantSlug && tenantSlug.trim() !== "") {
      return tenantSlug.trim();
    }
    return "default"; // only when route truly lacks tenant context
  }, [tenantSlug]);

  /**
   * =========================================================================
   * 🔐 (1) Authentication Gate
   * =========================================================================
   */
  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/${canonicalSlug}/login`}
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  /**
   * =========================================================================
   * 🔐 (2) Authorization Gate
   * =========================================================================
   */
  if (!isAuthorized) {
    return (
      <Navigate
        to={`/${canonicalSlug}/403`}
        replace
      />
    );
  }

  /**
   * =========================================================================
   * ✅ (3) Happy Path
   * =========================================================================
   */
  return children;
};

export default ProtectedRoute;